import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';

ChartJS.register(...registerables);

const TrainingsVsQuizzesChart = ({ data = {} }) => {
    const trainingChartRef = useRef(null);
    const trainingChartInstance = useRef(null);

    // Extract trend_data from props
    const { trend_data = [] } = data;

    // Default empty data structure - shows 0 values (not sample data)
    const defaultTrendData = [
        { label: 'Mon', trainings: 0, quizzes: 0 },
        { label: 'Tue', trainings: 0, quizzes: 0 },
        { label: 'Wed', trainings: 0, quizzes: 0 },
        { label: 'Thur', trainings: 0, quizzes: 0 },
        { label: 'Fri', trainings: 0, quizzes: 0 },
        { label: 'Sat', trainings: 0, quizzes: 0 },
    ];

    // Use API data if provided, otherwise show 0 values
    const chartTrendData = trend_data.length > 0 ? trend_data : defaultTrendData;
    const labels = chartTrendData.map(item => item.label || item.day);
    const trainingsData = chartTrendData.map(item => item.trainings || item.trainings_completed || 0);
    const quizzesData = chartTrendData.map(item => item.quizzes || item.quizzes_solved || 0);

    // Dynamic Y-axis max
    const maxVal = Math.max(...trainingsData, ...quizzesData, 0);
    const suggestedMax = maxVal === 0 ? 10 : Math.ceil(maxVal * 1.15);

    useEffect(() => {
        if (!trainingChartRef.current) return;

        if (trainingChartInstance.current) {
            trainingChartInstance.current.destroy();
        }

        const ctx = trainingChartRef.current.getContext('2d');
        if (!ctx) return;

        trainingChartInstance.current = new ChartJS(ctx, {
            type: 'line',
            data: {
                labels: labels,
                datasets: [
                    {
                        label: 'Trainings Completed',
                        data: trainingsData,
                        backgroundColor: 'rgba(235, 234, 255, 0.5)',
                        borderColor: '#1C13C3',
                        borderWidth: 2,
                        fill: true,
                        tension: 0,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                    },
                    {
                        label: 'Quizzes Solved',
                        data: quizzesData,
                        backgroundColor: 'rgba(234, 254, 207, 0.5)',
                        borderColor: '#6CB50A',
                        borderWidth: 2,
                        fill: true,
                        tension: 0,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                    padding: { top: 8, right: 8, bottom: 6, left: 8 },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: {
                            font: { size: 12, family: 'Arial, sans-serif' },
                            color: '#222222',
                            padding: 8,
                        },
                    },
                        y: {
                            beginAtZero: true,
                            suggestedMax: suggestedMax,
                            ticks: {
                                precision: 0,
                            font: { size: 12, family: 'Arial, sans-serif' },
                            color: '#444444',
                            padding: 8,
                        },
                        grid: { color: '#f0f0f0' },
                        border: { display: false },
                    },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            boxWidth: 20,
                            boxHeight: 10,
                            padding: 15,
                            font: { size: 11, family: 'Arial, sans-serif' },
                            color: '#333333',
                        },
                    },
                    tooltip: { enabled: true },
                    datalabels: { display: false },
                },
                interaction: { intersect: false, mode: 'index' },
            },
        });

        return () => {
            if (trainingChartInstance.current) {
                trainingChartInstance.current.destroy();
            }
        };
    }, [data, labels, trainingsData, quizzesData, suggestedMax]);

    // Always show chart - no "no data" message
    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
            <canvas ref={trainingChartRef} style={{ width: '100%', height: '100%' }} />
        </Box>
    );
};

export default TrainingsVsQuizzesChart;

